/**
 * Privacy policy — ships with the first feedback intake channel (F-13).
 *
 * Feedback section names the context envelope fields from context.ts explicitly,
 * states concrete retention windows, discloses Turnstile, and covers Cognito
 * account data the site already collects.
 */

import { PageHeader } from "../components/ui/PageHeader.js";

/**
 * Every field buildFeedbackContext() puts on the wire (web/src/lib/feedback/context.ts).
 * Keep this list in sync with that builder — do not invent names.
 */
const ENVELOPE_FIELDS: { key: string; label: string }[] = [
  { key: "version", label: "Context schema version (always 1)" },
  { key: "appId", label: "Application id (checklist)" },
  { key: "appVersion", label: "App version / deploy stamp" },
  { key: "surfaceId", label: "Which form / tool surface" },
  { key: "route", label: "Current page path (tokens redacted)" },
  { key: "occurredAt", label: "Timestamp when the report was built" },
  { key: "timezone", label: "Timezone" },
  { key: "locale", label: "Locale" },
  { key: "viewport", label: "Viewport size" },
  { key: "deviceClass", label: "Device class (mobile / tablet / desktop)" },
  { key: "browser", label: "Browser (family + major version)" },
  { key: "os", label: "Operating system" },
  { key: "prefersColorScheme", label: "Color scheme preference" },
  { key: "sessionRef", label: "Ephemeral browser-session id (not a login id)" },
  {
    key: "payload",
    label:
      "Optional allowlisted facts only — counts, kind, model id, cache-hit, HTTP status, or error name/message/truncated stack. Never list names, item text, notes, other members' emails, share tokens, or invite tokens",
  },
];

export function PrivacyPage() {
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:py-10">
      <PageHeader
        title="Privacy"
        subtitle="What NeatTick collects, how long we keep it, and how to ask us to delete it."
      />

      <p className="-mt-2 mb-6 text-xs text-muted-sm">Last updated: October 11, 2026</p>

      <div className="space-y-10 text-sm leading-relaxed text-muted">
        <section aria-labelledby="privacy-account">
          <h2 id="privacy-account" className="text-base font-semibold text-ink">
            Account data
          </h2>
          <p className="mt-2">
            When you create an account, AWS Cognito stores your email address and password
            credentials (or your Google account link if you sign in with Google). We also store
            a profile with your screen name and optional avatar image. Screen names appear to
            people you share checklists with. We use your email for sign-in, password reset, and
            sharing-related notices you trigger in the product.
          </p>
          <p className="mt-2">
            Lists, templates, and checklists you create are stored so the product can show them
            to you and to people you share them with. We do not sell this content.
          </p>
        </section>

        <section aria-labelledby="privacy-access-requests">
          <h2 id="privacy-access-requests" className="text-base font-semibold text-ink">
            Access requests
          </h2>
          <p className="mt-2">
            When sign-up is invite-only, the request form stores the email address you enter,
            an optional note, your IP address, and your browser&apos;s user agent. We use the IP
            address and user agent to spot automated abuse. The IP address and user agent are
            deleted when a request is approved. Pending and denied requests are deleted
            automatically 90 days after the last request.
          </p>
        </section>

        <section aria-labelledby="privacy-feedback">
          <h2 id="privacy-feedback" className="text-base font-semibold text-ink">
            Feedback and support messages
          </h2>
          <p className="mt-2">
            When you use the contact form, bug report, feature request, broken-link reporter, or
            thumbs controls, we create a tracked incident. Email is optional on every channel —
            leave it only if you want a reply. Name is optional and only collected on the contact
            form.
          </p>
          <p className="mt-2">
            Unless you choose to omit technical context, each report&apos;s context envelope
            includes these fields (from the client builder):
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {ENVELOPE_FIELDS.map((f) => (
              <li key={f.key}>
                <code className="rounded bg-slate-100 px-1 font-mono text-xs text-ink">{f.key}</code>
                {" — "}
                {f.label}
              </li>
            ))}
          </ul>
          <p className="mt-2">
            Share-link paths are redacted before they leave your browser (for example{" "}
            <code className="rounded bg-slate-100 px-1 font-mono text-xs text-ink">/s/[token]</code>
            ), so a capability token is not stored with the incident.
          </p>
        </section>

        <section aria-labelledby="privacy-assistant">
          <h2 id="privacy-assistant" className="text-base font-semibold text-ink">
            AI assistant
          </h2>
          <p className="mt-2">
            The in-app AI assistant is available to administrators today. When you use it, your
            messages, and the lists, templates, and checklists it reads to answer you, are
            processed by Anthropic Claude models, either through Amazon Bedrock (AWS) or directly
            through Anthropic&apos;s API, depending on the model an administrator has selected.
          </p>
          <p className="mt-2">
            Anthropic&apos;s commercial terms state that it may not train models on content sent
            through its API, and its published retention policy says API inputs and outputs are
            automatically deleted from its systems within 30 days, except where longer retention
            is needed to enforce its usage policy or to comply with the law. Amazon Bedrock
            processes the same models inside AWS under AWS&apos;s own terms.
          </p>
          <p className="mt-2">
            Separately, the &quot;Create with AI&quot; feature that drafts a list sends the purpose
            you type to the same models, through the same two routes, to generate the list&apos;s
            items.
          </p>
          <p className="mt-2">
            Conversation text is kept for 30 days. Card numbers, SSN-like numbers, and
            password-like strings are removed before storage. On sites where an administrator has
            switched conversation storage to metadata-only, only usage details are kept — never
            the text of the conversation.
          </p>
          <p className="mt-2">
            You can delete a conversation at any time from the assistant&apos;s history panel.
            Usage counts and thumbs-up/down ratings on assistant replies are kept in aggregate,
            not tied to conversation text.
          </p>
        </section>

        <section aria-labelledby="privacy-retention">
          <h2 id="privacy-retention" className="text-base font-semibold text-ink">
            Retention
          </h2>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>
              <span className="text-ink">Incident records:</span> 24 months
            </li>
            <li>
              <span className="text-ink">contactEmail / contactName:</span> 12 months after the
              incident closes (fields are purged; the row may remain)
            </li>
            <li>
              <span className="text-ink">Large context blobs:</span> 12 months
            </li>
            <li>
              <span className="text-ink">Rate-limit IP hashes:</span> up to 72 hours
            </li>
            <li>
              <span className="text-ink">AI assistant conversations:</span> 30 days (or usage
              details only, in metadata-only mode)
            </li>
          </ul>
          <p className="mt-2">
            Account data (Cognito credentials and your profile) is kept while your account exists.
            Deleting your account removes the profile and associated app data we control; Cognito
            deletion follows the same request path below.
          </p>
        </section>

        <section aria-labelledby="privacy-third-party">
          <h2 id="privacy-third-party" className="text-base font-semibold text-ink">
            Third-party scripts
          </h2>
          <p className="mt-2">
            Feedback forms may load{" "}
            <span className="text-ink">Cloudflare Turnstile</span> to reduce automated spam. That
            script runs in your browser when you open a form and sends a challenge token with the
            submission. See Cloudflare&apos;s own privacy documentation for how Turnstile processes
            data.
          </p>
          <p className="mt-2">
            Incidents are stored in a shared feedback service operated alongside this site. Owner
            notification and optional auto-reply email are sent via Amazon SES from{" "}
            <code className="rounded bg-slate-100 px-1 font-mono text-xs text-ink">
              noreply@neattick.com
            </code>
            .
          </p>
          <p className="mt-2">
            The AI assistant sends your messages and the CheckList content it reads to Anthropic
            Claude models, through Amazon Bedrock or directly through Anthropic&apos;s API, to
            generate its replies. List generation sends the purpose text you type to the same
            providers.
          </p>
        </section>

        <section aria-labelledby="privacy-deletion">
          <h2 id="privacy-deletion" className="text-base font-semibold text-ink">
            Deletion requests
          </h2>
          <p className="mt-2">
            To request deletion of feedback you submitted (or of your account and profile), use the{" "}
            <a href="/contact" className="font-medium text-brand-700 no-underline hover:underline">
              contact form
            </a>{" "}
            and include your ticket reference if you have one, or the email on the account. We will
            confirm when the deletion is done.
          </p>
        </section>

        <section aria-labelledby="privacy-changes">
          <h2 id="privacy-changes" className="text-base font-semibold text-ink">
            Changes
          </h2>
          <p className="mt-2">
            If this policy changes in a material way, we will update this page. Continued use of
            the site after a change means you have seen the updated terms.
          </p>
        </section>
      </div>
    </div>
  );
}
